<template>
  <modal-select
    ref="modalSelect"
    v-model="valueDirty"
    :items="filteredRepositoryTasks"
    :loading="repositoryTasks.loading"
    item-text="title"
    :dialog-title="dialogTitle"
    :force-dialog="forceDialog"
    :dialog-only="dialogOnly"
    :dialog-visible.sync="dialogVisibleDirty"
    v-bind="inputProps"
    :error-messages="errorMessagesComputed"
    @opened="focusSearch"
    @submit="$emit('submit')"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <div>{{ dialogTitle }}</div>
      <div
        v-if="dialogSubtitle"
        class="subtitle-1 mt-2 grey--text"
      >
        {{ dialogSubtitle }}
      </div>
    </template>
    <!--</editor-fold>-->
    <template
      v-if="!dialogOnly"
      v-slot:dropdownSelectItem="{ item: task }"
    >
      <v-list-item-content>
        <v-list-item-title>
          {{ task.title || 'n/a' }}
        </v-list-item-title>
      </v-list-item-content>
    </template>
    <template v-slot:filters>
      <v-row no-gutters>
        <v-col cols="8">
          <v-select
            v-model="filters.taskTypeId"
            class="mb-1"
            solo
            label="Search by type"
            clearable
            :items="taskTypes.data"
            :loading="taskTypes.loading"
            hide-details
            item-value="id"
            item-text="title"
          />
        </v-col>
        <v-col cols="8">
          <v-text-field
            ref="nameSearch"
            v-model="filters.title"
            placeholder="Search by title"
            clearable
            hide-details
            autocomplete="disable"
            solo
          />
        </v-col>
      </v-row>
    </template>
    <template v-slot:dialogItem="{ item: task, selected: selected, selectItem: selectItem }">
      <v-tooltip
        :key="task.id"
        top
      >
        <template v-slot:activator="{ on }">
          <v-list-item
            class="list--bordered v-list__tile--height-auto"
            :class="{ 'grey lighten-4': selected || (wintering && task.service_tasks.find(t => t.finished)) }"
            @click="wintering && task.service_tasks.find(t => t.finished) ? null : selectItem(task)"
            v-on="!!task.description ? on : undefined"
          >
            <v-list-item-content class="pa-2">
              <v-list-item-title :class="{ 'primary--text font-weight-bold': selected }">
                {{ task.title || 'n/a' }}
                <v-icon
                  v-if="task.description"
                  x-small
                >
                  fa fa-info-circle
                </v-icon>
                <span class="font-weight-light caption">
                  &bull; {{ task.service_task_type.title }}
                </span>
              </v-list-item-title>
              <!--          <v-list-item-subtitle-->
              <!--            class="caption"-->
              <!--            v-html="task.description"-->
              <!--          />-->
            </v-list-item-content>
            <v-list-item-action
              v-if="selected || (wintering && task.service_tasks.length)"
              class="align-self-start pt-4"
            >
              <v-avatar
                color="success"
                size="26px"
              >
                <v-icon
                  color="white"
                  small
                >
                  check
                </v-icon>
              </v-avatar>
            </v-list-item-action>
          </v-list-item>
        </template>
        <div
          v-if="task.description"
          v-html="task.description"
        />
      </v-tooltip>
    </template>
    <template v-slot:noData>
      There are no tasks
    </template>
  </modal-select>
</template>

<script>
  import ModalSelect from '@/components/helpers/ModalSelect'
  import ServiceTaskRepository from '@/models/ServiceTaskRepository'
  import ServiceTaskType from '@/models/ServiceTaskType'
  import inputProps, { extractProps } from '@/components/helpers/inputProps'

  export default {
    name: 'RepositoryTaskModalSelect',
    components: { ModalSelect },
    props: {
      ...inputProps,
      value: {
        type: Object,
        default: null
      },
      forceDialog: {
        type: Boolean,
        default: false
      },
      dialogOnly: {
        type: Boolean,
        default: false
      },
      wintering: {
        type: Boolean,
        default: false
      },
      yachtId: {
        type: Number,
        default: null
      },
      dialogVisible: {
        type: Boolean,
        default: false
      },
      dialogTitle: {
        type: String,
        default: 'Select task'
      },
      dialogSubtitle: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        repositoryTasks: {
          data: [],
          loading: false
        },
        taskTypes: {
          data: [],
          loading: false
        },
        filters: {
          title: null,
          taskTypeId: null
        }
      }
    },
    computed: {
      valueDirty: {
        set: function (value) {
          this.$emit('input', value)
        },
        get: function () {
          return this.value
        }
      },
      dialogVisibleDirty: {
        set: function (value) {
          this.$emit('update:dialogVisible', value)
        },
        get: function () {
          return this.dialogVisible
        }
      },
      inputProps() {
        return extractProps(this)
      },
      errorMessagesComputed() {
        // this hack is needed to prevent re-rendering (Vue bug)
        // https://github.com/vuejs/vue/issues/4060
        return this.errorMessages
      },
      filteredRepositoryTasks() {
        return this.repositoryTasks.data
          .filter(task => {
            return this.filters.taskTypeId ? task.service_task_type_id === this.filters.taskTypeId : true
          })
          .filter(task => {
            return this.filters.title ? (task.title.toLowerCase().includes(this.filters.title.toLowerCase())) : true
          })
      }
    },
    watch: {
      yachtId(val) {
        if (!val) return

        this.fetchRepositoryTasks()
      }
    },
    created() {
      this.fetchRepositoryTasks()
      this.fetchTaskTypes()
    },
    methods: {
      async fetchRepositoryTasks() {
        this.repositoryTasks.loading = true

        try {
          if (this.wintering) {
            this.repositoryTasks.data = this.yachtId ? await ServiceTaskRepository.api.getWinteringTasksForYacht(this.yachtId) : []
          } else {
            this.repositoryTasks.data = await ServiceTaskRepository.api.fetch()
          }
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.repositoryTasks.loading = false
        }
      },
      async fetchTaskTypes() {
        this.taskTypes.loading = true

        try {
          this.taskTypes.data = await ServiceTaskType.api.fetch()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.taskTypes.loading = false
        }
      },
      focusSearch() {
        this.$refs.nameSearch.$el.querySelector('input').focus()
      }
    }
  }
</script>

<style scoped>

</style>
